import { useEffect, useState } from 'react';

import { Drawer } from '@/components/Layout/Drawer';
import { FullPageLoader } from '@/components/Layout/FullPageLoader';

import { useTranslate } from '@/hooks/useTranslate';

import { FinanceQuestion } from '../hooks';
import { QuestionAssets } from './QuestionAssets';
import { QuestionIncomeAndSpending } from './QuestionIncomeAndSpending';
import { QuestionLiabilities } from './QuestionLiabilities';

type YourFinancesDrawerProps = {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  currentQuestion: FinanceQuestion;
};

export function YourFinancesDrawer({
  open,
  onClose,
  isLoading,
  currentQuestion: currentQuestionFromState,
}: Readonly<YourFinancesDrawerProps>) {
  const { t } = useTranslate();
  const [currentQuestion, setCurrentQuestion] = useState<FinanceQuestion>(
    FinanceQuestion.Assets,
  );

  useEffect(() => {
    setCurrentQuestion(currentQuestionFromState);
  }, [currentQuestionFromState]);

  const handleClose = () => {
    onClose();
  };

  const onNext = () => {
    if (currentQuestion === FinanceQuestion.IncomeAndSpending) {
      onClose();
      return;
    }
    setCurrentQuestion(prev => prev + 1);
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t('moreInfo.form.yourFinances.title')}
      headerBgColor={theme => theme.palette.neutral.min}
    >
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <YourFinancesQuestion question={currentQuestion} onNext={onNext} />
      )}
    </Drawer>
  );
}

interface YourFinancesQuestionProps {
  question: FinanceQuestion;
  onNext: () => void;
}

export function YourFinancesQuestion({
  question,
  onNext,
}: Readonly<YourFinancesQuestionProps>) {
  switch (question) {
    case FinanceQuestion.Assets:
      return <QuestionAssets onSubmit={onNext} />;
    case FinanceQuestion.Liabilities:
      return <QuestionLiabilities onSubmit={onNext} />;
    case FinanceQuestion.IncomeAndSpending:
      return <QuestionIncomeAndSpending onSubmit={onNext} />;
    default:
      return null;
  }
}
